import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ClientModal from './ClientModal';
import { updateClient, deleteClient } from '../../services/clientService';

const AVATAR_COLORS = ['#3B82F6', '#8B5CF6', '#14B8A6', '#F43F5E', '#F97316', '#EC4899', '#6366F1', '#0EA5E9'];

function getColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getIniciais(name) {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) return `Hoje, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  if (diff < 172800000) return `Ontem, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleDateString('pt-BR');
}

export default function ClientsTable({ clients }) {
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const [editCliente, setEditCliente] = useState(null);

  const filtered = clients.filter((c) =>
    [c.nome, c.email, c.telefone].some((v) => (v || '').toLowerCase().includes(busca.toLowerCase()))
  );

  const handleUpdate = async (dados) => {
    await updateClient(editCliente.id, dados);
  };

  const handleDelete = async (clientId) => {
    await deleteClient(clientId);
  };

  const handleSimular = (client) => {
    navigate('/simulador', { state: { clienteId: client.id } });
  };

  const tdStyle = { padding: '14px 20px', fontSize: '0.82rem', borderBottom: '1px solid var(--border)' };

  return (
    <>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)', gap: 10, flexWrap: 'wrap',
        }}>
          <input
            type="text" placeholder="Buscar cliente por nome, email ou telefone..."
            value={busca} onChange={(e) => setBusca(e.target.value)}
            style={{
              padding: '8px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-xs)',
              fontFamily: 'var(--font)', fontSize: '0.82rem', flex: 1, minWidth: 0, outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <Button variant="ghost">Filtrar</Button>
            <Button variant="ghost">Exportar</Button>
          </div>
        </div>

        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 750 }}>
          <thead>
            <tr>
              {['Cliente', 'Telefone', 'Valor Financiado', 'Prazo', 'Simulações', 'Status', 'Criado em', ''].map((h) => (
                <th key={h} style={{
                  textAlign: 'left', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 20px',
                  background: 'var(--surface)', borderBottom: '1px solid var(--border)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                  {busca ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado ainda.'}
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const cor = getColor(c.nome || '');
              const iniciais = getIniciais(c.nome || 'U');
              return (
                <tr key={c.id} style={{ cursor: 'pointer' }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: cor, flexShrink: 0,
                      }}>{iniciais}</div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.nome}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{c.email || '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>{c.telefone || '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{c.valorFinanciado || '—'}</td>
                  <td style={tdStyle}>{c.prazo ? `${c.prazo} meses` : '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{c.simulacoes || 0}</td>
                  <td style={tdStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', fontWeight: 500 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.status === 'active' ? 'var(--accent)' : 'var(--border-strong)' }} />
                      {c.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: '0.78rem', color: 'var(--text-muted)' }}>{formatDate(c.criadoEm)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => setEditCliente(c)} title="Editar" style={{
                        width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)',
                        background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.72rem', cursor: 'pointer',
                      }}>✏️</button>
                      <button onClick={() => handleSimular(c)} title="Simular" style={{
                        width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)',
                        background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.72rem', cursor: 'pointer',
                      }}>⚡</button>
                      <button
                        onClick={() => {
                          if (c.telefone) {
                            const num = c.telefone.replace(/\D/g, '');
                            window.open(`https://wa.me/55${num}`, '_blank');
                          }
                        }}
                        title="WhatsApp" style={{
                          width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)',
                          background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.72rem', cursor: 'pointer',
                        }}>💬</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </Card>

      <ClientModal
        open={!!editCliente}
        cliente={editCliente}
        onClose={() => setEditCliente(null)}
        onSave={handleUpdate}
        onDelete={handleDelete}
      />
    </>
  );
}